import React from "react"
import { useEffect, useState } from "react"
import { navigate } from "gatsby"
import { useStore } from "../../context/auth/auth"
import { formatRegions } from "../config/regionConfig"
import { profilesBaseUrl } from "../config/ecomConfig"
import getConfigTID from "../functions/getConfigTID"
import { refreshAuthToken } from "../functions/refreshAuthToken"
import Cookies from "js-cookie"

const {
  redirect_uri,
  code_verifier,
  client_id,
  url,
  client_secret,
} = getConfigTID()

const languageDefault = {
  nativeField: "English",
  translatableName: "",
  code: "en",
  localeCode: "en-US",
}

const useLanguage = (
  location,
  region,
  regionList,
  languages,
  pageType = null,
) => {
  const [state] = useStore()
  const [profile, setProfile] = useState(null)
  const [languageSettings, setLanguageSettings] = useState(null)

  const getLogin = () => {
      const login = JSON.parse(localStorage?.getItem("user-trial-data"))
      const accessToken = login?.id
      const userInfo = JSON.parse(localStorage.getItem("user_info"))
      const uuid = userInfo?.tid
      if (accessToken && uuid) {
        return {
          accessToken,
          uuid,
        }
      }
    return null
  }

  useEffect(() => {
    if (pageType) {
      return
    }
    if (typeof window !== "undefined") {
      // Check if site has multiple languages enabled
      const hasMultipleLanguages = languages && languages.length > 1
      
      const languageCodeInUrl =
        window?.location?.pathname?.split("/")?.length &&
        window?.location?.pathname?.split("/")?.length >= 1
          ? window?.location?.pathname?.split("/")[1]
          : ""
      
      // Check if user is authenticated
      const isUserAuthenticated = getLogin() !== null
      
      // Handle root path (/) - should always redirect to a language path
      const isRootPath = window?.location?.pathname === "/" || languageCodeInUrl === ""
      
      if (isRootPath) {
        // For sites without multiple languages, always use /en
        if (!hasMultipleLanguages) {
          window.location.href = `/en${window.location.search}`
          return
        }
        // For multi-language sites: authenticated users use preference, anonymous use 'en'
        // Wait for languageSettings to be initialized for authenticated users
        if (isUserAuthenticated && !languageSettings) {
          return
        }
        const targetLang = isUserAuthenticated && languageSettings 
          ? languageSettings.language?.code?.toLowerCase() 
          : "en"
        window.location.href = `/${targetLang}${window.location.search}`
        return
      }
      
      const isValidLanguage = languages?.some(lang => lang?.code?.toLowerCase() === languageCodeInUrl?.toLowerCase())
      
      // For sites without multiple languages
      if (!hasMultipleLanguages) {
        // If already on /en, do nothing
        if (languageCodeInUrl?.toLowerCase() === "en") {
          return
        }
        // Otherwise redirect to /en
        window.location.href = `/en${window.location.search}`
        return
      }
      
      // For multi-language sites, wait for languageSettings to be initialized
      if (!languageSettings) {
        return
      }
      
      const languageCode = languageSettings?.language?.code?.toLowerCase()
      
      // For multi-language sites with non-authenticated users and invalid locale, fallback to /en
      if (!isUserAuthenticated && !isValidLanguage && languageCodeInUrl) {
        window.location.href = `/en${window.location.search}`
        return
      }
      
      // For authenticated users on multi-language sites: redirect to their preferred language in TID
      if (hasMultipleLanguages && isUserAuthenticated && languageCodeInUrl !== languageCode) {
        let url = window?.location?.pathname
        let result = url.replace(languageCodeInUrl, languageCode)
        window.history.pushState({}, "", result + window.location.search)
        window.location.reload()
        return
      }
    }
  }, [languageSettings, pageType, languages])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentUrl = new URL(window?.location)
      const urlLangCode = currentUrl.searchParams.get("lang")
      if (urlLangCode) {
        let updatedLanguage
        languages?.forEach(language => {
          if (language?.code === urlLangCode) {
            // if language in ?lang param matches real language code, assign that as the new language
            updatedLanguage = language
          }
        })

        if (
          updatedLanguage?.nativeField !== undefined &&
          updatedLanguage?.code !== undefined
        ) {
          // update language selector with new language if the fields exist
          saveLanguageSettings({
            region: region?.region
              ? region.region
              : region?.name
              ? region.name
              : "",
            machineName: region?.machineName,
            language: updatedLanguage,
          })
        } else {
          // if lang value doesn't match, remove it from the URL and reload original page
          currentUrl.searchParams.delete("lang")
          navigate(`${currentUrl.toString()}`, { replace: true })
        }
      }
    }
  }, [languages, region])

  const updateProfileLanguage = async (newLanguage) => {
    const login = getLogin()
    if (login) {
      const languageCode = newLanguage?.language?.localeCode
      if (languageCode) {
        return fetch(
         `${profilesBaseUrl}/users/${login?.uuid}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${login?.accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              language: languageCode,
            }),
          }
        )
        .then(response => {
          if (response.status === 401) {
            refreshAuthToken(
              client_id,
              client_secret,
              redirect_uri,
              code_verifier,
              null,
              url,
              null,
              null,
              () => updateProfileLanguage(newLanguage),
              null
            )
          }
        })
        .catch(error => {
          console.log("error", error)
        })
      }
    }
  }

  const saveLanguageSettings = async (newLanguage) => {
    if (newLanguage) {

      await updateProfileLanguage(newLanguage)

      if (!newLanguage.machineName && formatRegions.length) {
        const y = formatRegions(regionList)?.find(reg => {
          return reg.name.toLowerCase() === newLanguage.region.toLowerCase()
        })
        if (y) {
          newLanguage["machineName"] = y.machineName
        }
      }
      newLanguage.language["code"] = newLanguage.language.code.toLowerCase()

      Cookies.set("languageSettings", JSON.stringify(newLanguage))

      let path = ""
      if (location?.pathname) {
        const slashIndex = location?.pathname?.indexOf("/", 1)
        path = slashIndex !== -1 ? location?.pathname?.slice(slashIndex) : ""
      }

      navigate(`/${newLanguage?.language?.code?.toLowerCase()}${path}${window.location.search}`, {
        replace: true,
      })
    }
  }

  useEffect(() => {
    const fetchProfile = async () => {
      const accessToken = JSON.parse(
        localStorage.getItem("user-trial-data")
      )?.id
      if (accessToken) {
        fetch(
          `${profilesBaseUrl}/me`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        )
          .then(response => response.json())
          .then(data => {
            setProfile(data)
          })
          .catch(error => {
            console.log("error", error)
          })
      }
    }

    fetchProfile()
  }, [state?.isAuthCheckComplete])

  useEffect(() => {
    const languageSettings = JSON.parse(
      Cookies.get("languageSettings") || null
    )

    // Check if user is authenticated
    const isUserAuthenticated = getLogin() !== null
    
    // For non-authenticated users, use the language from the URL
    let languageCode
    if (!isUserAuthenticated && typeof window !== "undefined") {
      const languageCodeInUrl = window?.location?.pathname?.split("/")[1]
      const urlLanguage = languages?.find(lang => lang?.code?.toLowerCase() === languageCodeInUrl?.toLowerCase())
      
      if (urlLanguage) {
        // Use the language from the URL for non-authenticated users
        languageCode = urlLanguage.localeCode
      } else {
        // Fallback to en-US if URL language is invalid
        languageCode = "en-US"
      }
    } else {
      // For authenticated users, use profile language or fallback to cookie/browser/default
      languageCode = profile?.language
        ? profile.language
        : languageSettings?.language?.localeCode
        ? languageSettings?.language?.localeCode
        : navigator?.language
        ? navigator.language
        : "en-US"
    }

    const language = languages?.find(languageEntry => languageEntry?.localeCode === languageCode) || languageDefault

    const newLanguageSettings = {
      region: region?.region ? region.region : region?.name ? region.name : "",
      machineName: region?.machineName,
      language: language,
    }

    setLanguageSettings(newLanguageSettings)
    
    // Only update cookie for authenticated users
    if (isUserAuthenticated) {
      Cookies.set("languageSettings", JSON.stringify(newLanguageSettings))
    }
  }, [profile, languages, region, state.isAuthCheckComplete, location?.pathname])

  return { languageSettings, saveLanguageSettings }
}

export default useLanguage